import {
  FLUXMED_OPENID_SCOPES,
} from "../constants/organizationStoreRegistry";
import type { WooStoreConfig } from "../types";
import { resolveWordPressSiteBase } from "./wordpressSiteBase";

function getOAuthAuthorizeBaseUrl(): string {
  const tokenApi = import.meta.env.VITE_ENDPOINT_token_api;
  if (tokenApi) {
    return tokenApi.replace(/\/oauth2\/token\/?$/i, "/oauth2/authorize");
  }
  const domain = import.meta.env.VITE_ENDPOINT_XDSB_domain ?? "https://dev.fluxmed.com.br";
  return `${domain}/oauth2/authorize`;
}

function resolveOpenIdClientId(store: WooStoreConfig): string {
  const clientId = store.openIdClientId?.trim();
  if (!clientId) {
    throw new Error("Dispensador sem client_id OpenID configurado.");
  }
  return clientId;
}

const DEFAULT_OPENID_REDIRECT_PATH =
  "/wp-admin/admin-ajax.php?action=openid-connect-authorize";

function buildOpenIdRedirectUri(store: WooStoreConfig): string {
  const base = resolveWordPressSiteBase(store.siteOrigin);
  const path = store.openIdRedirectPath?.trim() || DEFAULT_OPENID_REDIRECT_PATH;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/** Monta URL de autorização OpenID Fluxmed para associação na loja WordPress. */
export function buildFluxmedOpenIdLoginUrl(
  store: WooStoreConfig,
  state: string,
): string {
  const authorizeBase = getOAuthAuthorizeBaseUrl();
  const redirectUri = buildOpenIdRedirectUri(store);
  const params = new URLSearchParams({
    response_type: "code",
    scope: FLUXMED_OPENID_SCOPES,
    client_id: resolveOpenIdClientId(store),
    state,
    redirect_uri: redirectUri,
  });
  return `${authorizeBase}?${params.toString()}`;
}

/**
 * Abre popup de login OpenID e resolve quando o popup é fechado.
 * Fallback: se popup bloqueado, redireciona a janela atual.
 */
export function openFluxmedOpenIdLogin(store: WooStoreConfig): Promise<void> {
  return new Promise((resolve) => {
    const state = crypto.randomUUID();
    const url = buildFluxmedOpenIdLoginUrl(store, state);
    const popup = window.open(
      url,
      "FluxmedOpenIdLogin",
      "width=600,height=800,scrollbars=yes",
    );

    if (!popup) {
      window.location.href = url;
      resolve();
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        resolve();
      }
    }, 500);
  });
}

/** URL de pagamento com redirect via wp-login (padrão fluxmed.cloud). */
export function buildPaymentLoginRedirectUrl(
  store: WooStoreConfig,
  paymentUrl: string,
): string {
  const base = resolveWordPressSiteBase(store.siteOrigin);
  return `${base}/wp-login.php?redirect_to=${encodeURIComponent(paymentUrl)}`;
}

export function openPaymentPopup(
  store: WooStoreConfig,
  paymentUrl: string,
): void {
  const loginRedirectUrl = buildPaymentLoginRedirectUrl(store, paymentUrl);
  window.open(
    loginRedirectUrl,
    `Pagamento-${store.id}`,
    "width=600,height=800,scrollbars=yes",
  );
}

/** Abre popup de pagamento e retorna referência da janela (null se bloqueado). */
export function openPaymentPopupWindow(
  store: WooStoreConfig,
  paymentUrl: string,
): Window | null {
  const loginRedirectUrl = buildPaymentLoginRedirectUrl(store, paymentUrl);
  return window.open(
    loginRedirectUrl,
    `Pagamento-${store.id}-${Date.now()}`,
    "width=600,height=800,scrollbars=yes",
  );
}

/** Abre popup de pagamento e resolve quando o usuário fechar a janela. */
export function openPaymentPopupMonitored(
  store: WooStoreConfig,
  paymentUrl: string,
): Promise<{ closed: true }> {
  return new Promise((resolve) => {
    const popup = openPaymentPopupWindow(store, paymentUrl);

    if (!popup) {
      openPaymentPopup(store, paymentUrl);
      resolve({ closed: true });
      return;
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        resolve({ closed: true });
      }
    }, 500);
  });
}
